import { BarChart } from '../../charts/BarChart';
import type { ChartDatum } from '../../charts/chartTypes';
import { ChartModelHelper } from '../../helpers/ChartModelHelper';
import { ReportFormatHelper } from '../../helpers/ReportFormatHelper';
import type { IComponentFacts } from '../../types/report';
import { EmptyFacts } from '../EmptyFacts';
import { CURRENCY_AXIS_WIDTH, moneyFormatter } from './chartFormatters';
import { useChartStyle } from './useChartStyle';

interface ICashBridgeChartProps {
  facts: IComponentFacts;
}

/** Stacked series: an invisible base lifts each visible bar to the running total. */
const SERIES = {
  base: 'Base',
  balance: 'Balance',
  increase: 'Increase',
  decrease: 'Decrease',
};

export const CashBridgeChart = ({ facts }: ICashBridgeChartProps) => {
  const style = useChartStyle(1);
  const steps = ChartModelHelper.cashBridge(facts.current);
  if (steps.length === 0) {
    return (
      <EmptyFacts message="Cash bridge needs an opening balance sheet; the first closed month has none." />
    );
  }
  const data: ChartDatum[] = steps.map((s) => ({
    label: s.label,
    [SERIES.base]: s.base,
    [SERIES.balance]: s.kind !== 'delta' ? s.height : 0,
    [SERIES.increase]: s.kind === 'delta' && s.value >= 0 ? s.height : 0,
    [SERIES.decrease]: s.kind === 'delta' && s.value < 0 ? s.height : 0,
  }));
  return (
    <BarChart
      {...style}
      className="h-full"
      data={data}
      index="label"
      type="stacked"
      categories={Object.values(SERIES)}
      colors={['transparent', 'blue', 'emerald', 'rose']}
      valueFormatter={moneyFormatter(facts.currency)}
      yAxisWidth={CURRENCY_AXIS_WIDTH}
      showLegend={false}
      customTooltip={({ active, label }) => {
        const step = steps.find((s) => s.label === label);
        if (!active || !step) return null;
        return (
          <div className="rounded-md border border-border-secondary bg-bg-primary px-lg py-sm text-sm shadow-md">
            <p className="font-medium text-text-primary">{step.label}</p>
            <p className="tabular-nums text-text-secondary">
              {ReportFormatHelper.currency(step.value, facts.currency)}
            </p>
          </div>
        );
      }}
    />
  );
};
